import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import TransformationModal from './TransformationModal';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../support/store';
import {
  addTransformation,
  removeTransformation,
  setQueryResult,
  updateTransformation,
} from '../../../support/slices/tileBuilderSlice';
import { getPipelineStepOutputs, getTransformationDisplayName } from '../../../support/customTileTransformUtils';
import { getOp } from '../../../support/transformations/registry';
import { executeQueriesWithChaining } from '../../../support/queryChainExecutor';
import { getLocalJsonFileByPath } from '../../../support/localFileRegistry';

// --- Helpers ---
function substituteVariables(str: string, variables: Record<string, string>) {
  return str.replace(/\{(\w+)\}/g, (_, k) => variables[k] ?? `{${k}}`);
}

function configSummary(type: string, config: any) {
  const op = getOp(type);
  if (op?.summary) return op.summary(config || {});
  if (!config) return '';
  return Object.entries(config)
    .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
    .join(', ');
}

function sourcesSummary(queryIndices: number[], queries: any[]) {
  if (!queryIndices || queryIndices.length === 0) return '';
  return queryIndices
    .map((idx) =>
      idx < queries.length
        ? `Query ${idx + 1} (${queries[idx]?.model || 'Unknown'})`
        : `Transform ${idx - queries.length + 1}`,
    )
    .join(', ');
}

function getEmailPrefix(value: string | undefined | null) {
  if (!value) return '';
  return String(value).split('@')[0].trim().toLowerCase();
}

function resolveLocalFileData(fileData: any, rawPath: string, availableVariables: Record<string, string>) {
  if (fileData == null) return undefined;

  if (!rawPath || !rawPath.trim()) {
    return fileData;
  }

  const substitutedPath = substituteVariables(rawPath, availableVariables).trim();
  if (!substitutedPath) return fileData;

  const arrayData = Array.isArray(fileData) ? fileData : [fileData];
  const lastSegment = substitutedPath.split('/').filter(Boolean).pop() || '';
  const emailPrefix = getEmailPrefix(lastSegment);

  if (!emailPrefix) return fileData;

  const matchedUser = arrayData.find((item: any) => {
    const empCecid = String(item?.EMP_CECID ?? '').toLowerCase();
    const empCecidDisp = String(item?.EMP_CECID_DISP ?? '').toLowerCase();
    const leaderCecid = String(item?.LDR_CECID ?? '').toLowerCase();

    return (
      empCecid === emailPrefix ||
      empCecidDisp === emailPrefix ||
      leaderCecid === emailPrefix
    );
  });

  if (!matchedUser) return fileData;
  if (Array.isArray(matchedUser?.reports)) return matchedUser.reports;
  return matchedUser;
}

function getResolvedQueryResult(q: any, availableVariables: Record<string, string>) {
  if (q?.dataSource === 'Local File') {
    const file = getLocalJsonFileByPath(q.localFilePath);
    return resolveLocalFileData(file?.data, q.path || '', availableVariables) ?? null;
  }

  return q.functionResult ?? q.queryResult ?? null;
}

const Transformations = ({ chartId }: { chartId: string }) => {
  const queriesRaw = useSelector((state: RootState) => state.tileBuilder[chartId]?.tile?.queries || []);
  const transformations = useSelector((state: RootState) => state.tileBuilder[chartId]?.tile?.transformations || []);
  const userData: any = useSelector((state: RootState) => state.user?.userData);
  const previewRuntimeContext: any = useSelector(
    (state: RootState) => state.tileBuilder[chartId]?.preview?.runtimeContext || {}
  );
  const dispatch = useAppDispatch();

  const availableVariables = useMemo(
    () => ({
      name: userData?.name ?? '',
      email: userData?.email ?? '',
      sfId: userData?.sfId ?? '',
      current_time_filter: previewRuntimeContext?.current_time_filter ?? 'all',
    }),
    [userData?.name, userData?.email, userData?.sfId, previewRuntimeContext?.current_time_filter],
  );

  const queriesEffective = useMemo(
    () =>
      queriesRaw.map((q: any) => ({
        ...q,
        path: substituteVariables(q.path || '', availableVariables),
        params: Object.fromEntries(
          Object.entries(q.params || {}).map(([k, v]) => [k, substituteVariables(String(v), availableVariables)]),
        ),
        payload: typeof q.payload === 'string' ? substituteVariables(q.payload, availableVariables) : q.payload,
        queryResult: getResolvedQueryResult(q, availableVariables) ?? [],
      })),
    [queriesRaw, availableVariables],
  );

  const allQueriesHaveSomeResultField = useMemo(() => {
    return queriesRaw.length > 0 && queriesRaw.every((q: any) => getResolvedQueryResult(q, availableVariables) != null);
  }, [queriesRaw, availableVariables]);

  const pipelineOutputs = useMemo(() => {
    try {
      return allQueriesHaveSomeResultField ? getPipelineStepOutputs(queriesEffective, transformations) : [];
    } catch (e) {
      console.error('Pipeline outputs error', e);
      return [];
    }
  }, [queriesEffective, transformations, allQueriesHaveSomeResultField]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [isOpeningModal, setIsOpeningModal] = useState(false);

  const buildSourcesForModal = (upTo: number | null) => {
    const effectiveUpTo = upTo === null ? transformations.length : upTo;

    const querySources = queriesRaw.map((q: any) => getResolvedQueryResult(q, availableVariables));

    const previousTransformations = transformations.slice(0, effectiveUpTo);
    const transformSources =
      previousTransformations.length > 0
        ? getPipelineStepOutputs(queriesEffective, previousTransformations, previousTransformations.length).map(
          (o: any) => o ?? null,
        )
        : [];

    return [...querySources, ...transformSources];
  };

  const forceExecuteQueries = async () => {
    if (!queriesEffective.length) return;

    setIsOpeningModal(true);
    try {
      const nonLocalOriginalIndices = queriesEffective
        .map((q: any, index: number) => ({ q, index }))
        .filter(({ q }: any) => q?.dataSource !== 'Local File');

      if (nonLocalOriginalIndices.length > 0) {
        const queriesToExecute = nonLocalOriginalIndices.map(({ q }) => q);

        const results = await executeQueriesWithChaining({
          queries: queriesToExecute,
          availableVariables,
        });

        results.forEach((result: any, filteredIndex: number) => {
          const originalIndex = nonLocalOriginalIndices[filteredIndex]?.index;

          if (originalIndex == null) return;

          dispatch(
            setQueryResult({
              chartId,
              index: originalIndex,
              result: result.transformedResult ?? result.rawResult ?? [],
            }),
          );
        });
      }

      queriesEffective.forEach((q: any, index: number) => {
        if (q?.dataSource !== 'Local File') return;

        const resolved = getResolvedQueryResult(q, availableVariables);
        dispatch(
          setQueryResult({
            chartId,
            index,
            result: resolved,
          }),
        );
      });
    } catch (e) {
      console.error('forceExecuteQueries error', e);
    } finally {
      setIsOpeningModal(false);
    }
  };

  const handleAddTransformation = async () => {
    await forceExecuteQueries();
    setEditingIdx(null);
    setModalOpen(true);
  };

  const handleEditTransformation = async (index: number) => {
    await forceExecuteQueries();
    setEditingIdx(index);
    setModalOpen(true);
  };

  const handleModalSubmit = (transformationDef: any) => {
    const newTransformation = {
      transformation: {
        type: transformationDef.type,
        config: transformationDef.type === 'script' ? {} : transformationDef.config || {},
        ...(transformationDef.type === 'script' && { logic: transformationDef.logic }),
      },
      queryIndices: transformationDef.queryIndices,
    };

    if (editingIdx !== null) {
      dispatch(updateTransformation({ chartId, index: editingIdx, transformation: newTransformation }));
    } else {
      dispatch(addTransformation({ chartId, transformation: newTransformation }));
    }

    setModalOpen(false);
    setEditingIdx(null);
  };

  const handleDeleteTransformation = (index: number) => {
    dispatch(removeTransformation({ chartId, index }));
  };

  const initialTransformation =
    editingIdx !== null
      ? {
        type: transformations[editingIdx].transformation.type,
        config: transformations[editingIdx].transformation.config,
        logic: transformations[editingIdx].transformation.logic,
        queryIndices: transformations[editingIdx].queryIndices,
      }
      : undefined;

  const modalSources = modalOpen
    ? buildSourcesForModal(editingIdx === null ? 0 : editingIdx)
    : [];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Data Transformations
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define JavaScript functions or use predefined operations to transform the raw data fetched from your queries.
        Transformations are applied in the order they appear below.
      </Typography>

      {isOpeningModal && (
        <Typography color="info.main" sx={{ mb: 2 }}>
          Preparing preview data...
        </Typography>
      )}

      <List component={Paper} sx={{ mb: 2 }}>
        {transformations.length === 0 ? (
          <ListItem>
            <ListItemText secondary="No transformations added yet." />
          </ListItem>
        ) : (
          transformations.map((transformation: any, index: number) => {
            const { type, config, logic } = transformation.transformation;
            const sources = sourcesSummary(transformation.queryIndices, queriesEffective);

            let secondary = `Sources: ${sources}`;
            if (type === 'script') {
              secondary +=
                '\nScript: ' +
                (logic
                  ? logic
                  .split('\n')
                  .map((line: string) => line.trim())
                  .filter(Boolean)
                  .join(' ')
                  .substring(0, 80) + (logic.length > 80 ? '...' : '')
                  : '[empty]');
            } else if (type) {
              const confSummary = configSummary(type, config);
              if (confSummary) secondary += `\n${confSummary}`;
            }

            return (
              <ListItem
                key={index}
                secondaryAction={
                  <>
                    <IconButton edge="end" aria-label="edit" onClick={() => handleEditTransformation(index)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteTransformation(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                }
              >
                <ListItemText
                  primary={getTransformationDisplayName(transformation)}
                  secondary={secondary}
                  sx={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
                />
              </ListItem>
            );
          })
        )}
      </List>

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAddTransformation}
        disabled={queriesEffective.length === 0 || isOpeningModal}
      >
        Add New Transformation
      </Button>

      <Accordion expanded={debugOpen} onChange={(_, expanded) => setDebugOpen(expanded)} sx={{ mt: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body1">Debug Queries & Transformations</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            <Typography variant="subtitle2">availableVariables:</Typography>
            <pre>{JSON.stringify(availableVariables, null, 2)}</pre>

            <Typography variant="subtitle2">queriesRaw[0].queryResult:</Typography>
            <pre>{JSON.stringify(queriesRaw?.[0]?.queryResult, null, 2)}</pre>

            <Typography variant="subtitle2">queriesEffective:</Typography>
            <pre>{JSON.stringify(queriesEffective, null, 2)}</pre>

            <Typography variant="subtitle2">transformations:</Typography>
            <pre>{JSON.stringify(transformations, null, 2)}</pre>

            <Typography variant="subtitle2">modalSources[0]:</Typography>
            <pre>{JSON.stringify(modalSources?.[0], null, 2)}</pre>

            <Typography variant="subtitle2">pipelineOutputs:</Typography>
            <pre>{JSON.stringify(pipelineOutputs, null, 2)}</pre>
          </Box>
        </AccordionDetails>
      </Accordion>

      {modalOpen && (
        <TransformationModal
          key={editingIdx === null ? 'add' : `edit-${editingIdx}`}
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingIdx(null);
          }}
          onSubmit={handleModalSubmit}
          queries={queriesEffective}
          sources={modalSources}
          initialTransformation={initialTransformation}
          availableVariables={availableVariables}
        />
      )}
    </Box>
  );
};

export default Transformations;